import React, { createContext, useState, useContext } from 'react';
import { toast } from 'react-toastify';

const BasketContext = createContext();

export const useBasket = () => useContext(BasketContext);

export const BasketProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [shippingOption, setShippingOption] = useState('email'); // 'email' or 'post'
    const [discount, setDiscount] = useState(null); // { code, type, value, amount } or null
    const [discountStatus, setDiscountStatus] = useState('idle'); // 'idle' | 'checking' | 'error'
    const [discountError, setDiscountError] = useState(null);

    const addToBasket = (product) => {
        setItems(prevItems => {
            const existingItem = prevItems.find(item => item.id === product.id);
            if (existingItem) {
                // If it is, increase quantity
                return prevItems.map(item =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            // If not, add new item with quantity 1
            return [...prevItems, { ...product, quantity: 1 }];
        });
        toast.success(`${product.name} has been added to your basket!`);
    };

    const removeFromBasket = (productId) => {
        setItems(prevItems => prevItems.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        setItems(prevItems =>
            prevItems.map(item =>
                item.id === productId ? { ...item, quantity: Math.max(0, quantity) } : item
            ).filter(item => item.quantity > 0) // Remove item if quantity is 0
        );
    };

    const clearBasket = () => {
        setItems([]);
        setDiscount(null);
        setDiscountStatus('idle');
        setDiscountError(null);
    };

    const removeDiscountCode = () => {
        setDiscount(null);
        setDiscountStatus('idle');
        setDiscountError(null);
    };

    // Asks the backend to validate a discount code against the real, current
    // basket contents - the backend recomputes prices itself, so this can't be
    // spoofed into a bigger discount than the code actually allows.
    const applyDiscountCode = async (code) => {
        if (!code || !code.trim()) return;
        setDiscountStatus('checking');
        setDiscountError(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/discount-codes/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code,
                    shippingOption,
                    items: items.map(item => ({ id: item.id, n: item.name.substring(0, 20), q: item.quantity })),
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setDiscount(null);
                setDiscountStatus('error');
                setDiscountError(data.error || 'This discount code could not be applied.');
                return;
            }
            setDiscount(data.discount);
            setDiscountStatus('idle');
            toast.success(`Discount code "${data.discount.code}" applied!`);
        } catch (err) {
            setDiscount(null);
            setDiscountStatus('error');
            setDiscountError('Could not check that code right now. Please try again.');
        }
    };

    const itemCount = items.reduce((total, item) => total + item.quantity, 0);

    const value = {
        items,
        addToBasket,
        removeFromBasket,
        updateQuantity,
        clearBasket,
        itemCount,
        shippingOption,
        setShippingOption,
        discount,
        discountStatus,
        discountError,
        applyDiscountCode,
        removeDiscountCode,
    };

    return (
        <BasketContext.Provider value={value}>{children}</BasketContext.Provider>
    );
};